import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  AdminBusinessSearchResult,
  AdminVoicePhoneAssignmentValidation,
  AdminVoicePhoneNumber,
} from "../../api/admin";
import {
  useAdminBusinessSearch,
  useAssignAdminVoicePhoneNumber,
  useValidateAdminVoicePhoneAssignment,
} from "../../hooks/useVoicePhoneNumbers";

interface VoiceNumberAssignDialogProps {
  number: AdminVoicePhoneNumber | null;
  onClose: () => void;
  onAssigned: () => void;
}

function businessLabel(business: AdminBusinessSearchResult) {
  const shortId = business.business_id.slice(0, 8);
  const voice = business.voice_config_status ?? "no voice config";
  return `${business.name} · voice ${voice} · ${shortId}`;
}

function issueList(
  title: string,
  issues: Array<{ code: string; message: string }>
) {
  if (issues.length === 0) return null;
  return (
    <Alert severity={title === "Warnings" ? "warning" : "error"}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 0.5 }}>
        {title}
      </Typography>
      <Stack component="ul" sx={{ m: 0, pl: 2 }}>
        {issues.map((issue) => (
          <Typography component="li" key={issue.code} sx={{ fontSize: "0.78rem" }}>
            {issue.message}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

export default function VoiceNumberAssignDialog({
  number,
  onClose,
  onAssigned,
}: VoiceNumberAssignDialogProps) {
  const [query, setQuery] = useState("");
  const [business, setBusiness] = useState<AdminBusinessSearchResult | null>(null);
  const [confirmRegionWarning, setConfirmRegionWarning] = useState(false);
  const [allowCooldownOverride, setAllowCooldownOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [validation, setValidation] =
    useState<AdminVoicePhoneAssignmentValidation | null>(null);

  const businessSearch = useAdminBusinessSearch(query);
  const validateMutation = useValidateAdminVoicePhoneAssignment();
  const assignMutation = useAssignAdminVoicePhoneNumber();

  useEffect(() => {
    if (number) {
      setQuery("");
      setBusiness(null);
      setConfirmRegionWarning(false);
      setAllowCooldownOverride(false);
      setOverrideReason("");
      setValidation(null);
    }
  }, [number]);

  useEffect(() => {
    setValidation(null);
  }, [number?.voice_phone_number_id, business?.business_id, confirmRegionWarning, allowCooldownOverride]);

  const canValidate = Boolean(number && business);
  const canAssign = Boolean(
    number &&
      business &&
      validation?.can_assign &&
      (!allowCooldownOverride || overrideReason.trim())
  );

  const needsRegionConfirmation = useMemo(
    () =>
      Boolean(number?.country_code === "AU" && (!number.region || number.region === "unknown")) ||
      Boolean(validation?.blocking_errors.some((issue) => issue.code === "au_region_unknown")),
    [number, validation]
  );

  const needsCooldownOverride = useMemo(
    () =>
      number?.status === "cooldown" ||
      Boolean(validation?.blocking_errors.some((issue) => issue.code === "number_in_cooldown")),
    [number, validation]
  );

  const validate = async () => {
    if (!number || !business) return;
    const result = await validateMutation.mutateAsync({
      voicePhoneNumberId: number.voice_phone_number_id,
      businessId: business.business_id,
      confirmRegionWarning,
      allowCooldownOverride,
    });
    setValidation(result);
  };

  const assign = async () => {
    if (!number || !business || !validation?.can_assign) return;
    const result = await assignMutation.mutateAsync({
      voicePhoneNumberId: number.voice_phone_number_id,
      businessId: business.business_id,
      confirmRegionWarning,
      allowCooldownOverride,
      reason: overrideReason.trim() || undefined,
    });
    if (result.ok) {
      onAssigned();
      onClose();
    } else {
      setValidation(null);
    }
  };

  return (
    <Dialog open={Boolean(number)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Assign number</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.25, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: "0.76rem", color: "#94A3B8" }}>
            Selected number
          </Typography>
          <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
            {number?.phone_number}
          </Typography>
        </Box>

        <Autocomplete
          options={businessSearch.data ?? []}
          loading={businessSearch.isFetching}
          value={business}
          onChange={(_, value) => setBusiness(value)}
          inputValue={query}
          onInputChange={(_, value) => setQuery(value)}
          getOptionLabel={businessLabel}
          isOptionEqualToValue={(option, value) => option.business_id === value.business_id}
          noOptionsText={query.trim().length < 2 ? "Type at least 2 characters" : "No businesses found"}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Business"
              size="small"
              helperText="Search by business name, owner email, or business ID."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {businessSearch.isFetching ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.business_id}>
              <Box>
                <Typography sx={{ fontSize: "0.84rem", fontWeight: 700 }}>
                  {option.name}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                  {option.owner_email ?? "No owner email"} · voice {option.voice_config_status ?? "no voice config"} · {option.business_id.slice(0, 8)}
                </Typography>
              </Box>
            </Box>
          )}
        />

        {needsRegionConfirmation && (
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmRegionWarning}
                onChange={(event) => setConfirmRegionWarning(event.target.checked)}
              />
            }
            label="I confirm the region warning has been reviewed."
          />
        )}

        {needsCooldownOverride && (
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowCooldownOverride}
                  onChange={(event) => setAllowCooldownOverride(event.target.checked)}
                />
              }
              label="I understand this number was recently assigned to another business."
            />
            {allowCooldownOverride && (
              <TextField
                label="Cooldown override reason"
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                size="small"
                multiline
                minRows={2}
                required
              />
            )}
          </Stack>
        )}

        <Button
          variant="outlined"
          onClick={validate}
          disabled={!canValidate || validateMutation.isPending}
          sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}
        >
          Validate assignment
        </Button>

        {validation && (
          <Stack spacing={1.25}>
            {issueList("Blocking errors", validation.blocking_errors)}
            {issueList("Warnings", validation.warnings)}
            {validation.can_assign && (
              <Alert severity="success">
                Assignment is valid for the selected number and business.
              </Alert>
            )}
          </Stack>
        )}

        {assignMutation.data?.ok === false && (
          <Alert severity="error">
            {assignMutation.data.message ?? "Assignment was blocked. Refreshing inventory may be required."}
          </Alert>
        )}
        {assignMutation.isError && <Alert severity="error">Failed to assign this number.</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={assign}
          disabled={!canAssign || assignMutation.isPending}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Assign number
        </Button>
      </DialogActions>
    </Dialog>
  );
}
